/* import __COLOCATED_TEMPLATE__ from './report-container.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import {
  FIN_AI_COPILOT_ID,
  FIN_FOR_PLATFORMS_CORE_ID,
  PROACTIVE_SUPPORT_PLUS_ID,
} from 'embercom/lib/billing';
import type IntlService from 'ember-intl/addon/services/intl';
import type Product from 'embercom/models/product';
import type UsageReminderModel from 'embercom/models/usage-reminder';
import type Contract from 'embercom/models/billing/contract';
import ajax from 'embercom/lib/ajax';
import moment from 'moment-timezone';
import { PERFORMANCE_PRO_ID } from '@intercom/billing-core/constants-strings';

const REPORTS = [
  'full-seats',
  'copilot-seats',
  'resolutions',
  'fin-conversations',
  'emails-sent',
  'messages-sent',
  'sms',
  'whatsapp_outbound_only',
  'voice-resolutions',
  'fin-voice-conversations',
  'phone',
  'conversations',
  'qualifications',
] as const;

type Report = (typeof REPORTS)[number];

interface Args {
  contract: Contract;
  pmdasData: $TSFixMe;
  currentBillingPeriodCharges: $TSFixMe;
  usageReminders: Array<UsageReminderModel>;
  usageLimits: Array<{ metric: Metric; tableName: string; limit: $TSFixMe }>;
}

interface Signature {
  Args: Args;
  Element: never;
}

export default class ReportContainer extends Component<Signature> {
  @service declare appService: AppService;
  @service declare customerService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare permissionsService: $TSFixMe;

  @tracked currentActiveMetrics: any[] = [];

  // pnp-addproduct pnp-addmetric
  constructor(owner: any, args: Args) {
    super(owner, args);
    taskFor(this.fetchCurrentActiveMetricsTask).perform();
  }

  get reportsFiltered() {
    return REPORTS.filter((report) => this.shouldIncludeReport(report));
  }

  private shouldIncludeReport(report: Report) {
    switch (report) {
      case 'resolutions':
        return this.hasMetricInSubscription(
          Metric.resolutions_with_custom_answers,
          Metric.resolutions,
        );
      case 'fin-conversations':
        return this.hasMetricInSubscription(Metric.fin_conversations);
      case 'full-seats':
      case 'emails-sent':
      case 'sms':
      case 'whatsapp_outbound_only':
        return !this.appService.app.canUseStandalone;
      case 'phone':
        return (
          !this.appService.app.canUseStandalone ||
          this.hasMetricInSubscription(Metric.voice_resolutions)
        );
      case 'messages-sent':
        return !this.appService.app.canUseStandalone && this.hasAddOn(PROACTIVE_SUPPORT_PLUS_ID);
      case 'copilot-seats':
        if (
          this.appService.app.canUseStandalone &&
          !this.appService.app.canUseCopilotOnStandalone
        ) {
          return false;
        }
        return this.hasAddOn(FIN_AI_COPILOT_ID) || this.hasFinForPlatformProduct;
      case 'voice-resolutions':
        return this.hasMetricInSubscription(Metric.voice_resolutions);
      case 'fin-voice-conversations':
        return this.hasMetricInSubscription(Metric.fin_voice_conversations);
      case 'conversations':
        return this.hasAddOn(PERFORMANCE_PRO_ID);
      case 'qualifications':
        return (
          this.appService.app.canUseFinSalesAgent &&
          this.hasMetricInSubscription(Metric.qualifications)
        );
      default:
        return true;
    }
  }

  get metrics() {
    return this.reportsFiltered.map((report) => {
      let reportMetricMap: Record<string, Metric[]> = this.getReportMetricMap();

      if (report === 'whatsapp_outbound_only') {
        return {
          type: report,
          pricingMetrics: reportMetricMap['whatsapp'],
          titleTranslationKey: `${this.translationKey(report)}.title`,
        };
      }

      return {
        type: report,
        pricingMetrics: reportMetricMap[report],
        titleTranslationKey: `${this.translationKey(report)}.title`,
      };
    });
  }

  private translationKey(report: Report) {
    if (report === 'resolutions') {
      return 'billing.usage.pricing-metric.outcomes';
    }
    if (report === 'voice-resolutions') {
      return 'billing.usage.pricing-metric.voice-outcomes';
    }

    return `billing.usage.pricing-metric.${report}`;
  }

  getReportMetricMap() {
    return {
      'full-seats': [Metric.core_seat_count],
      'copilot-seats': [Metric.copilot_seat_count],
      'emails-sent': [Metric.emails_sent],
      'messages-sent': [Metric.messages_sent],
      sms: [
        ...(this.appService.app.isSalesforceContracted
          ? [Metric.sms_segments_sent_received_in_shifted_billing_cycle_au]
          : []),
        Metric.sms_segments_sent_received_in_shifted_billing_cycle_us,
        Metric.sms_segments_sent_received_in_shifted_billing_cycle_ca,
        Metric.sms_segments_sent_received_in_shifted_billing_cycle_uk,
      ],
      whatsapp: [Metric.whatsapp_outbound],
      resolutions: [Metric.resolutions_with_custom_answers],
      'fin-conversations': [Metric.fin_conversations],
      'voice-resolutions': [Metric.voice_resolutions], // pnp-addproduct pnp-addmetric
      'fin-voice-conversations': [Metric.fin_voice_conversations],
      phone: [Metric.calling],
      conversations: [Metric.conversations],
      qualifications: [Metric.qualifications],
    };
  }

  hasAddOn(addOn: string) {
    return this.appService.app.activeProducts.some((product: Product) => product.id === addOn);
  }

  get hasFinForPlatformProduct() {
    return this.appService.app.activeProducts.some(
      (product: Product) => product.id === FIN_FOR_PLATFORMS_CORE_ID,
    );
  }

  get customer() {
    return this.customerService.customer;
  }

  get subscription() {
    return this.customer.subscription;
  }

  get totalNumberOfWorkspaces() {
    return (this.args.contract?.numberOfSecondarySubscriptions ?? 0) + 1;
  }

  get isRenewalMonth() {
    return this.args.currentBillingPeriodCharges.is_renewal_month;
  }

  get cadence() {
    return this.args.currentBillingPeriodCharges.cadence;
  }

  get hasAmendmentDate() {
    return !!this.args.currentBillingPeriodCharges.amendment_date;
  }

  get formattedAmendmentDate() {
    let amendmentDate = moment.utc(this.args.currentBillingPeriodCharges.amendment_date);
    return amendmentDate.format('MMMM D, YYYY');
  }

  get inTrial() {
    return this.customerService.customer?.inTrial;
  }

  get shouldShowRenewalBanner() {
    return (
      this.permissionsService.currentAdminCan('can_access_billing_settings') &&
      this.appService.app.canUseBillingSummaryRedesign &&
      !this.inTrial &&
      this.isRenewalMonth
    );
  }

  @dropTask
  *fetchCurrentActiveMetricsTask(): any {
    try {
      let response = yield ajax({
        url: '/ember/billing/current_active_pricing_metrics',
        type: 'GET',
        data: {
          app_id: this.appService.app.id,
        },
      });
      this.currentActiveMetrics = response || [];
    } catch (error) {
      console.error('Failed to fetch current active metrics:', error);
      this.currentActiveMetrics = [];
    }
  }
  private hasMetricInSubscription(...metrics: Metric[]) {
    return this.currentActiveMetrics.some((m: any) => metrics.includes(m.metric_key));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Billing::Usage::ReportContainer': typeof ReportContainer;
  }
}
